import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import { toast } from "sonner";
import {
  isAnthropometryStructuredRecord,
  isClinicalStructuredRecord,
  isClinicalStructuredRecordIncludingMain,
  isMainStructuredRecord,
  isMedicalReportStructuredRecord,
} from "@/lib/clinical-records";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDataStale } from "@/contexts/DataStaleContext";
import {
  deleteTimelineItem,
  getPhysioSessionIds,
  getTimelineDeleteDescription,
} from "@/services/timeline-delete";
import {
  Activity,
  ClipboardList,
  ChevronDown,
  Dumbbell,
  FileText,
  HeartPulse,
  Image,
  Loader2,
  Microscope,
  Ruler,
  Sparkles,
  Stethoscope,
  Trash2,
  User,
} from "lucide-react";
import { RecordResultsModal } from "./RecordResultsModal";
import { GalleryImageModal } from "./GalleryImageModal";
import { SmartfileBiopsyDetails } from "./SmartfileBiopsyDetails";

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  file_path?: string | null;
  patient_id?: string | null;
  status?: string | null;
}

interface GalleryImage {
  id: string;
  file_name: string;
  file_path: string;
  ai_summary?: string | null;
  created_at: string;
  published_at: string | null;
  professional_notes: string | null;
}

interface PilatesEvolutionRecord {
  id: string;
  session_at: string;
  notes: string;
  created_at: string;
}

interface HeartRiskCalculationRecord {
  id: string;
  created_at: string;
  model: string | null;
  category: "low" | "borderline" | "intermediate" | "high" | string | null;
  result_snapshot: {
    tenYearRisk?: number;
    optimalTenYearRisk?: number;
    lifetimeRisk?: number | null;
    category?: string;
  } | null;
  input_snapshot: Record<string, unknown> | null;
}

interface TrainingPlanTimelineEvent {
  id: string;
  patient_id: string;
  plan_name: string;
  status: string | null;
  frequency_per_week: number | null;
  weekly_minutes: number | null;
  ai_recommendations: unknown;
  created_at: string;
}

interface TrainingPlanDetail {
  id: string;
  planName: string;
  frequencyPerWeek: number | null;
  weeklyMinutes: number | null;
  selectedObjectives: string[];
  summary: string | null;
  items: TrainingPlanDetailItem[];
}

interface TrainingPlanDetailItem {
  id: string;
  week_number: number;
  day_of_week: string;
  session_title: string;
  exercises: Array<Record<string, unknown>>;
  duration_minutes: number | null;
  intensity: string | null;
  notes: string | null;
  display_order: number;
}

type TimelineSource = "smartfile" | "trainingplan" | "gallery" | "pilates" | "heartrisk";

/** Linhas de observação (tabela Evolução) agrupadas por dia na timeline */
interface PhysioObservationLine {
  displayDate: string;
  text: string;
  eventDate: string;
  sortKey: number;
}

/** Linhas de dados (antropometria, SPPB, dinamometria, goniometria) no mesmo card */
interface PhysioDataLine {
  label: string;
  value: string;
  eventDate: string;
  sortKey: number;
}

interface PhysioDayGroup {
  dateKey: string;
  displayDate: string;
  observationLines: PhysioObservationLine[];
  dataLines: PhysioDataLine[];
}

interface PhysioGlobalGroup {
  days: PhysioDayGroup[];
  totalObservationCount: number;
  totalDataCount: number;
}

interface TimelineItem {
  id: string;
  source: TimelineSource;
  eventDate: string;
  year: number;
  contentTypeLabel: string;
  title: string;
  textSnippet: string;
  metricLabel: string;
  metricValue: string;
  metricCount: number;
  sessionId?: string;
  metrics: Array<{
    label: string;
    value: string;
    eventDate: string;
  }>;
  /** Quando presente, o card concentra toda a evolução fisioterápica no modal */
  physioGlobalGroup?: PhysioGlobalGroup;
  /** Galeria: abre modal de visualização */
  galleryImageId?: string;
  galleryFilePath?: string;
  heartRiskCalculationId?: string;
  pilatesRecordId?: string;
  considerations?: string | null;
  /** Biópsia: vários structured_records agrupados por data — rodapé abre detalhe da sessão */
  isBiopsyGrouped?: boolean;
  trainingPlanId?: string;
}

interface TimelineTypeStyle {
  badgeClassName: string;
  cardClassName: string;
  Icon: typeof Activity;
}

const defaultTypeStyle: TimelineTypeStyle = {
  badgeClassName: "border-slate-200 bg-slate-50 text-slate-700",
  cardClassName: "border-slate-200 hover:border-slate-300",
  Icon: ClipboardList,
};

const timelineTypeStyleMap: Record<string, TimelineTypeStyle> = {
  "Exame Laboratorial": {
    badgeClassName: "border-blue-200 bg-blue-50 text-blue-700",
    cardClassName: "border-blue-300/90 hover:border-blue-400",
    Icon: Microscope,
  },
  "Evolução Pilates": {
    badgeClassName: "border-cyan-200 bg-cyan-50 text-cyan-700",
    cardClassName: "border-cyan-300/90 hover:border-cyan-400",
    Icon: Dumbbell,
  },
  "Evolução Fisioterapêutica": {
    badgeClassName: "border-amber-200 bg-amber-50 text-amber-700",
    cardClassName: "border-amber-300/90 hover:border-amber-400",
    Icon: Activity,
  },
  "Sinais Vitais": {
    badgeClassName: "border-rose-200 bg-rose-50 text-rose-700",
    cardClassName: "border-rose-300/90 hover:border-rose-400",
    Icon: HeartPulse,
  },
  Antropometria: {
    badgeClassName: "border-violet-200 bg-violet-50 text-violet-700",
    cardClassName: "border-violet-300/90 hover:border-violet-400",
    Icon: Ruler,
  },
  Anamnese: {
    badgeClassName: "border-emerald-200 bg-emerald-50 text-emerald-700",
    cardClassName: "border-emerald-300/90 hover:border-emerald-400",
    Icon: ClipboardList,
  },
  "Laudo Médico": {
    badgeClassName: "border-indigo-200 bg-indigo-50 text-indigo-700",
    cardClassName: "border-indigo-300/90 hover:border-indigo-400",
    Icon: Stethoscope,
  },
  "Registro Clínico": {
    badgeClassName: "border-emerald-200 bg-emerald-50 text-emerald-700",
    cardClassName: "border-emerald-300/90 hover:border-emerald-400",
    Icon: ClipboardList,
  },
  Biópsia: {
    badgeClassName: "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700",
    cardClassName: "border-fuchsia-300/90 hover:border-fuchsia-400",
    Icon: FileText,
  },
  Anatomopatológico: {
    badgeClassName: "border-purple-200 bg-purple-50 text-purple-700",
    cardClassName: "border-purple-300/90 hover:border-purple-400",
    Icon: FileText,
  },
  "Plano de Treino": {
    badgeClassName: "border-teal-200 bg-teal-50 text-teal-700",
    cardClassName: "border-teal-300/90 hover:border-teal-400",
    Icon: Dumbbell,
  },
  "Plano Alimentar": {
    badgeClassName: "border-lime-200 bg-lime-50 text-lime-700",
    cardClassName: "border-lime-300/90 hover:border-lime-400",
    Icon: ClipboardList,
  },
  Imagem: {
    badgeClassName: "border-sky-200 bg-sky-50 text-sky-700",
    cardClassName: "border-sky-300/90 hover:border-sky-400",
    Icon: Image,
  },
  "Risco Cardiovascular": {
    badgeClassName: "border-red-200 bg-red-50 text-red-700",
    cardClassName: "border-red-300/90 hover:border-red-400",
    Icon: HeartPulse,
  },
};

interface PatientTimelineProps {
  patientId: string;
  sessions: SmartfileSession[];
  trainingPlans?: TrainingPlanTimelineEvent[];
  onRefresh?: () => void | Promise<void>;
  supabaseClient?: SupabaseClient<Database>;
  canDelete?: boolean;
}

function getRecordEventDate(record: any, sessionCreatedAt: string): string {
  return (
    record?.event_date ||
    record?.date ||
    record?.start_date ||
    record?.session_at ||
    sessionCreatedAt
  );
}

function isAnthropometryLikeRecord(record: any): boolean {
  return isAnthropometryStructuredRecord(record);
}

function getAnthropometryMetric(record: any): { label: string; value: string } | null {
  if (!isAnthropometryLikeRecord(record)) return null;
  const label = String(record?.measurement_type || record?.measurement_type_slug || "Métrica");
  const rawValue = record?.value ?? record?.measurement_value;
  const value = rawValue != null && rawValue !== "" ? String(rawValue) : "";
  const unit = record?.unit != null && record?.unit !== "" ? String(record.unit) : "";
  const formattedValue = [value, unit].filter(Boolean).join(" ").trim();
  return { label, value: formattedValue || "—" };
}

function getMetric(record: any): { label: string; value: string } {
  if (record?.type === "lab_result" && record?.value !== undefined && record?.value !== null) {
    return { label: record.test_name || "Exame", value: `${record.value}${record.unit ? ` ${record.unit}` : ""}` };
  }
  if (record?.type === "vital_sign") {
    const value = [record?.systolic && record?.diastolic ? `${record.systolic}/${record.diastolic}` : null, record?.value]
      .filter(Boolean)
      .join(" ");
    if (value) return { label: record?.name || "Sinal vital", value: `${value}${record?.unit ? ` ${record.unit}` : ""}` };
  }
  if (record?.type === "anthropometry") {
    if (record?.bmi) return { label: "IMC", value: String(record.bmi) };
    if (record?.weight) return { label: "Peso", value: `${record.weight} kg` };
  }
  const anthropometryMetric = getAnthropometryMetric(record);
  if (anthropometryMetric) return anthropometryMetric;
  if (record?.type === "training_plan" && record?.goal) {
    return { label: "Objetivo", value: String(record.goal) };
  }
  if (record?.type === "anamnesis" && (!record?.structure_type || record?.structure_type === "anamnesis")) {
    return {
      label: "Tipo",
      value: record?.note_type || "Nota clínica",
    };
  }
  return { label: "Métrica", value: "Sem métrica" };
}

function getAdditionalMetrics(record: any): Array<{ label: string; value: string; eventDate: string }> {
  const candidates = [record?.metrics, record?.results, record?.items, record?.measurements];
  const firstArray = candidates.find((candidate) => Array.isArray(candidate)) as any[] | undefined;
  if (!firstArray) return [];

  return firstArray
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => ({
      label: String(entry?.label || entry?.name || entry?.title || "Métrica"),
      value: String(entry?.value ?? entry?.result ?? entry?.description ?? "-"),
      eventDate: String(entry?.event_date || entry?.date || record?.event_date || record?.created_at || new Date().toISOString()),
    }))
    .filter((entry) => entry.value && entry.value !== "-");
}

/** Métricas exibíveis de um registro (lista aninhada ou métrica principal) — usado ao agrupar antropometria por data */
function collectMetricsForRecord(record: any, sessionCreatedAt: string): Array<{ label: string; value: string; eventDate: string }> {
  const eventDate = getRecordEventDate(record, sessionCreatedAt);
  const anthropometryMetric = getAnthropometryMetric(record);
  if (anthropometryMetric && anthropometryMetric.value !== "—") {
    return [{ ...anthropometryMetric, eventDate }];
  }
  const additionalMetrics = getAdditionalMetrics(record);
  if (additionalMetrics.length > 0) return additionalMetrics;
  const metric = getMetric(record);
  return [{ label: metric.label, value: metric.value, eventDate }];
}

function isMedicalReportRecord(record: any): boolean {
  return isMedicalReportStructuredRecord(record);
}

function getMedicalReportMetrics(record: any, sessionCreatedAt: string): Array<{ label: string; value: string; eventDate: string }> {
  const eventDate = getRecordEventDate(record, sessionCreatedAt);

  if (record?.structure_type === "metrics") {
    const label = String(record?.metric_name || record?.metric_name_slug || "Métrica");
    const rawValue = record?.value ?? record?.result ?? record?.measurement_value;
    const unit = record?.unit != null && record?.unit !== "" ? ` ${record.unit}` : "";
    const value = rawValue != null && rawValue !== "" ? `${rawValue}${unit}` : "Sem valor";

    return [{ label, value, eventDate }];
  }

  const summaryCandidates = [
    record?.classification,
    record?.conclusions,
    record?.observations,
    record?.technique,
    getTextSnippet(record),
  ].filter((entry) => typeof entry === "string" && entry.trim().length > 0) as string[];

  return [
    {
      label: String(record?.report_type || "Laudo"),
      value: summaryCandidates[0] || "Sem valor",
      eventDate,
    },
  ];
}

function isClinicalRecordRecord(record: any): boolean {
  return isClinicalStructuredRecord(record);
}

function getClinicalRecordMetrics(
  record: any,
  sessionCreatedAt: string,
): Array<{ label: string; value: string; eventDate: string }> {
  const eventDate = getRecordEventDate(record, sessionCreatedAt);

  if (record?.structure_type === "metrics") {
    const label = String(record?.metric_name || record?.metric_name_slug || record?.name || "Métrica");
    const rawValue = record?.value ?? record?.result ?? record?.measurement_value;
    const unit = record?.unit != null && record?.unit !== "" ? ` ${record.unit}` : "";
    const value = rawValue != null && rawValue !== "" ? `${rawValue}${unit}` : "Sem valor";

    return [{ label, value, eventDate }];
  }

  const summaryCandidates = [
    record?.category,
    record?.findings,
    record?.conclusion,
    record?.clinical_history,
    record?.clinical_note,
    record?.description,
    record?.notes,
    record?.observations,
    record?.conclusions,
    getTextSnippet(record),
  ].filter((entry) => typeof entry === "string" && entry.trim().length > 0) as string[];

  return [
    {
      label: String(record?.note_type || "Registro"),
      value: summaryCandidates[0] || "Sem valor",
      eventDate,
    },
  ];
}

function getTextSnippet(record: any, sessionSummary?: string | null): string {
  if (isClinicalStructuredRecord(record)) {
    const clinicalRecordCandidates = [
      record?.category,
      record?.findings,
      record?.conclusion,
      record?.clinical_history,
      record?.clinical_note,
      record?.description,
      record?.notes,
      record?.observations,
      sessionSummary,
    ].filter((item) => typeof item === "string" && item.trim().length > 0) as string[];

    return clinicalRecordCandidates[0] || "Sem descrição";
  }

  if (record?.type === "anamnesis" && (!record?.structure_type || record?.structure_type === "anamnesis")) {
    const clinicalCandidates = [
      record?.clinical_note,
      record?.description,
      record?.notes,
      record?.observations,
      sessionSummary,
    ].filter((item) => typeof item === "string" && item.trim().length > 0) as string[];

    return clinicalCandidates[0] || "Sem descrição";
  }

  if (record?.type === "physiotherapy_evolution") {
    const physioCandidates = [
      record?.observations,
      record?.description,
      record?.notes,
      record?.summary,
      record?.finding,
      record?.diagnosis,
      record?.impression,
      record?.content,
      record?.plan_name,
      record?.test_name,
      sessionSummary,
    ].filter((item) => typeof item === "string" && item.trim().length > 0) as string[];

    return physioCandidates[0] || "Sem descrição";
  }

  const candidates = [
    record?.description,
    record?.notes,
    record?.observations,
    record?.summary,
    record?.finding,
    record?.diagnosis,
    record?.impression,
    record?.content,
    record?.plan_name,
    record?.test_name,
    sessionSummary,
  ].filter((item) => typeof item === "string" && item.trim().length > 0) as string[];
  return candidates[0] || "Sem descrição";
}

function getContentTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    lab_result: "Exame Laboratorial",
    vital_sign: "Sinais Vitais",
    physiotherapy_evolution: "Evolução Fisioterapêutica",
    anthropometry: "Antropometria",
    anamnesis: "Anamnese",
    clinical_record: "Registro Clínico",
    clinical_note: "Registro Clínico",
    exam: "Registro Clínico",
    report: "Registro Clínico",
    medical_report: "Laudo Médico",
    biopsy_report: "Biópsia",
    anatomopathology: "Anatomopatológico",
    training_plan: "Plano de Treino",
    meal_plan: "Plano Alimentar",
  };
  return labels[type] || "Registro Clínico";
}

function getContentTypeLabelFromRecord(record: any): string {
  if (record?.type === "anamnesis" && record?.structure_type === "antropometry") {
    return "Antropometria";
  }
  if (record?.type === "physiotherapy_evolution" && record?.structure_type === "antropometry") {
    return "Antropometria";
  }
  if (isClinicalStructuredRecord(record)) {
    return "Registro Clínico";
  }
  return getContentTypeLabel(record?.type);
}

function getTrainingPlanIdFromRecord(record: any): string | null {
  const candidates = [record?.training_plan_id, record?.plan_id, record?.planId, record?.id];
  const valid = candidates.find((value) => typeof value === "string" && value.trim().length > 0);
  return typeof valid === "string" ? valid.trim() : null;
}

function getTrainingPlanSummary(plan: TrainingPlanTimelineEvent): string {
  const aiRecommendations =
    typeof plan.ai_recommendations === "object" && plan.ai_recommendations !== null
      ? (plan.ai_recommendations as Record<string, unknown>)
      : null;
  const summary = aiRecommendations?.summary;
  if (typeof summary === "string" && summary.trim().length > 0) {
    return summary.trim();
  }
  return "Plano de treino gerado no aplicativo.";
}

function toTrimmedString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function getExerciseRowData(exercise: Record<string, unknown>): { name: string; details: string; notes: string | null } {
  const name =
    toTrimmedString(exercise.name) ||
    toTrimmedString(exercise.exercise) ||
    toTrimmedString(exercise.exercicio) ||
    toTrimmedString(exercise.title) ||
    "Exercício";

  const sets = toTrimmedString(exercise.sets) || toTrimmedString(exercise.series);
  const reps = toTrimmedString(exercise.reps) || toTrimmedString(exercise.repeticoes);
  const load = toTrimmedString(exercise.load) || toTrimmedString(exercise.carga) || toTrimmedString(exercise.weight);
  const rest = toTrimmedString(exercise.rest) || toTrimmedString(exercise.descanso) || toTrimmedString(exercise.rest_seconds);
  const notes = toTrimmedString(exercise.notes) || toTrimmedString(exercise.obs) || toTrimmedString(exercise.observacao);

  const details = [
    sets ? `${sets} séries` : null,
    reps ? `${reps} reps` : null,
    load ? `Carga: ${load}` : null,
    rest ? `Descanso: ${rest}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    name,
    details: details || "Sem detalhes",
    notes,
  };
}

function isPhysioSubRecord(record: any): boolean {
  return (
    record?.type === "physiotherapy_evolution" &&
    record?.is_main_record !== true &&
    record?.is_main_record !== "true"
  );
}

function isPhysioObservationRow(record: any): boolean {
  return !record?.structure_type || record.structure_type === "physiotherapy_evolution";
}

function isPhysioDataRow(record: any): boolean {
  const st = record?.structure_type;
  return st === "antropometry" || st === "sppb_tests" || st === "dynamometry_tests" || st === "goniometria";
}

/** Data civil local yyyy-MM-dd para agrupar linhas da mesma DATA */
function normalizeDateKey(raw: string): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "invalid";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dateKeyToSortableIso(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0, 0).toISOString();
}

function normalizeDateKeyFromRecord(record: any, sessionCreatedAt: string): string {
  const raw = getRecordEventDate(record, sessionCreatedAt);
  let key = normalizeDateKey(raw);
  if (key === "invalid") key = normalizeDateKey(sessionCreatedAt);
  return key === "invalid" ? normalizeDateKey(new Date().toISOString()) : key;
}

function sortKeyForRecord(record: any, sessionCreatedAt: string, index: number): number {
  const raw = getRecordEventDate(record, sessionCreatedAt);
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? index : t + index * 1e-6;
}

function buildPhysioObservationLine(record: any, sessionCreatedAt: string, sortKey: number): PhysioObservationLine {
  const raw = getRecordEventDate(record, sessionCreatedAt);
  const dateObj = new Date(raw);
  const eventDate = Number.isNaN(dateObj.getTime()) ? sessionCreatedAt : raw;
  const displayDate = Number.isNaN(dateObj.getTime())
    ? "—"
    : format(dateObj, "dd/MM/yyyy", { locale: ptBR });

  const obs = typeof record.observations === "string" && record.observations.trim() ? record.observations.trim() : "";
  const parts: string[] = [];
  if (record.exercise) parts.push(`Exercício: ${record.exercise}`);
  if (record.load != null && record.load !== "")
    parts.push(`Carga: ${record.load}${record.load_unit ? ` ${record.load_unit}` : ""}`);
  if (record.reps != null && record.reps !== "") parts.push(`Repetições: ${record.reps}`);
  if (record.result) parts.push(`Resultado: ${record.result}`);
  const secondary = parts.join(" · ");
  const text = obs ? (secondary ? `${obs}\n${secondary}` : obs) : secondary || "—";

  return { displayDate, text, eventDate, sortKey };
}

function buildPhysioDataLine(record: any, sessionCreatedAt: string, sortKey: number): PhysioDataLine | null {
  const raw = getRecordEventDate(record, sessionCreatedAt);
  const eventDate = Number.isNaN(new Date(raw).getTime()) ? sessionCreatedAt : raw;
  const st = record?.structure_type;

  if (st === "antropometry") {
    const label = String(record.measurement_type || record.measurement_type_slug || "Dado");
    const val = record.value != null && record.value !== "" ? String(record.value) : "—";
    const u = record.unit ? String(record.unit) : "";
    return { label, value: u ? `${val} ${u}` : val, eventDate, sortKey };
  }

  if (st === "sppb_tests") {
    const name = record.test_name || "SPPB";
    const timePart = record.time != null ? `${record.time} s` : "";
    const scoreParts = [record.balance_score, record.gait_score, record.chair_score, record.total_score]
      .filter((x: unknown) => x != null && x !== "")
      .join(" / ");
    const value = [timePart, scoreParts ? `Pontos: ${scoreParts}` : "", record.classification || ""]
      .filter(Boolean)
      .join(" · ") || "—";
    return { label: name, value, eventDate, sortKey };
  }

  if (st === "dynamometry_tests") {
    const label = record.test_name || "Dinamometria";
    const r = record.right_value != null ? `D: ${record.right_value}` : "";
    const l = record.left_value != null ? `E: ${record.left_value}` : "";
    const value = [r, l, record.observation].filter(Boolean).join(" · ") || "—";
    return { label, value, eventDate, sortKey };
  }

  if (st === "goniometria") {
    const label = [record.movement_type, record.body_part, record.side].filter(Boolean).join(" · ") || "Goniometria";
    const gv = record.goniometry_value != null ? String(record.goniometry_value) : "—";
    const u = record.goniometry_unit || "°";
    return { label, value: `${gv} ${u}`.trim(), eventDate, sortKey };
  }

  return null;
}

function normalizeExamName(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function seededHash(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function pickStableRandomExamNames(examNames: string[], sessionId: string, maxItems = 5): string[] {
  if (examNames.length <= maxItems) return examNames;

  const keyed = examNames.map((name, index) => ({
    name,
    key: seededHash(`${sessionId}:${name}:${index}`),
  }));

  keyed.sort((a, b) => a.key - b.key);
  return keyed.slice(0, maxItems).map((entry) => entry.name);
}

function buildNonLabTimelineItem(session: SmartfileSession, record: any, stableIndex: number): TimelineItem {
  const eventDate = getRecordEventDate(record, session.created_at);
  const metric = getMetric(record);
  const additionalMetrics = getAdditionalMetrics(record);
  const metricCount = additionalMetrics.length > 1 ? additionalMetrics.length : 1;
  const trainingPlanId = record?.type === "training_plan" ? getTrainingPlanIdFromRecord(record) : undefined;

  return {
    id: `${session.id}-${record?.id ?? stableIndex}`,
    source: "smartfile",
    eventDate,
    year: new Date(eventDate).getFullYear(),
    contentTypeLabel: getContentTypeLabelFromRecord(record),
    title: record?.title || record?.name || session.file_name || getContentTypeLabelFromRecord(record),
    textSnippet: getTextSnippet(record, session.ai_summary),
    metricLabel: metric.label,
    metricValue: metric.value,
    metricCount,
    sessionId: session.id,
    metrics: additionalMetrics.length > 0 ? additionalMetrics : [{ label: metric.label, value: metric.value, eventDate }],
    trainingPlanId,
  };
}

const HEART_RISK_CATEGORY_LABEL_PT: Record<string, string> = {
  low: "Baixo risco",
  borderline: "Risco limítrofe",
  intermediate: "Risco intermediário",
  high: "Alto risco",
};

function formatHeartRiskPercent(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return `${value.toFixed(1)}%`;
}

function buildTrainingPlanTimelineItem(plan: TrainingPlanTimelineEvent): TimelineItem {
  const eventDate = plan.created_at;
  const hasFrequency = typeof plan.frequency_per_week === "number" && plan.frequency_per_week > 0;
  const hasWeeklyMinutes = typeof plan.weekly_minutes === "number" && plan.weekly_minutes > 0;
  const metricValue = [
    hasFrequency ? `${plan.frequency_per_week}x/semana` : null,
    hasWeeklyMinutes ? `${plan.weekly_minutes} min/semana` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    id: `training-plan-${plan.id}`,
    source: "trainingplan" as const,
    eventDate,
    year: new Date(eventDate).getFullYear(),
    contentTypeLabel: "Plano de Treino",
    title: plan.plan_name || "Plano de Treino",
    textSnippet: getTrainingPlanSummary(plan),
    metricLabel: "Frequência",
    metricValue: metricValue || "Sem frequência definida",
    metricCount: 1,
    metrics: [{ label: "Frequência", value: metricValue || "Sem frequência definida", eventDate }],
    trainingPlanId: plan.id,
  };
}

function pickLatestActiveTrainingPlan(plans: TrainingPlanTimelineEvent[]): TrainingPlanTimelineEvent | null {
  const activePlans = plans.filter((plan) => plan.status !== "archived");
  if (activePlans.length === 0) return null;

  return activePlans.reduce((latest, plan) =>
    new Date(plan.created_at).getTime() > new Date(latest.created_at).getTime() ? plan : latest,
  );
}

function dedupeTrainingPlanStructuredItems(
  structuredItems: TimelineItem[],
  hasCanonicalTrainingPlan: boolean,
): TimelineItem[] {
  const nonTrainingItems = structuredItems.filter((item) => item.contentTypeLabel !== "Plano de Treino");
  if (hasCanonicalTrainingPlan) {
    return nonTrainingItems;
  }

  const trainingStructuredItems = structuredItems.filter((item) => item.contentTypeLabel === "Plano de Treino");
  if (trainingStructuredItems.length <= 1) {
    return structuredItems;
  }

  const latestTrainingItem = trainingStructuredItems.reduce((latest, item) =>
    new Date(item.eventDate).getTime() > new Date(latest.eventDate).getTime() ? item : latest,
  );

  return [...nonTrainingItems, latestTrainingItem];
}

function toTimelineItems(
  sessions: SmartfileSession[],
  trainingPlans: TrainingPlanTimelineEvent[],
  galleryImages: GalleryImage[],
  pilatesRecords: PilatesEvolutionRecord[],
  heartRiskCalculations: HeartRiskCalculationRecord[],
): TimelineItem[] {
  const globalPhysioEntries: Array<{ record: any; index: number; sessionCreatedAt: string; globalIndex: number }> = [];
  const structuredItems: TimelineItem[] = sessions.flatMap((session) => {
    if (!Array.isArray(session.structured_records)) return [];
    const validRecords = session.structured_records.filter(
      (record: any) =>
        record &&
        typeof record === "object" &&
        (!isMainStructuredRecord(record) || isClinicalStructuredRecordIncludingMain(record)),
    );
    const labRecords = validRecords.filter((record: any) => record?.type === "lab_result");
    const nonLabRecords = validRecords.filter((record: any) => {
      if (record?.type === "lab_result") return false;
      return true;
    });

    const labItems: TimelineItem[] = [];
    if (labRecords.length > 0) {
      const eventDate = getRecordEventDate(labRecords[0], session.created_at);
      const uniqueExamNames = Array.from(
        new Set(
          labRecords
            .map((record: any) => normalizeExamName(record?.test_name))
            .filter((name): name is string => Boolean(name)),
        ),
      );
      const selectedExamNames = pickStableRandomExamNames(uniqueExamNames, session.id, 5);
      const labSnippet =
        selectedExamNames.length > 0
          ? selectedExamNames.join(", ")
          : "Resultados laboratoriais consolidados";

      labItems.push({
        id: `${session.id}-lab-result-group`,
        source: "smartfile",
        eventDate,
        year: new Date(eventDate).getFullYear(),
        contentTypeLabel: getContentTypeLabel("lab_result"),
        title: session.file_name || getContentTypeLabel("lab_result"),
        textSnippet: labSnippet,
        metricLabel: "Métrica",
        metricValue: "Sem métrica",
        metricCount: labRecords.length,
        sessionId: session.id,
        metrics: [],
      });
    }

    const physioEntries = nonLabRecords
      .map((record: any, index: number) => ({ record, index }))
      .filter(({ record }) => isPhysioSubRecord(record));

    for (const entry of physioEntries) {
      globalPhysioEntries.push({
        record: entry.record,
        index: entry.index,
        sessionCreatedAt: session.created_at,
        globalIndex: globalPhysioEntries.length,
      });
    }

    const otherNonLab = nonLabRecords.filter((record: any) => !isPhysioSubRecord(record));

    const anthroEntries = otherNonLab
      .map((record: any, index: number) => ({ record, index }))
      .filter(({ record }) => getContentTypeLabelFromRecord(record) === "Antropometria");

    const restNonLab = otherNonLab.filter((record: any) => getContentTypeLabelFromRecord(record) !== "Antropometria");

    const biopsyEntries = restNonLab
      .map((record: any, index: number) => ({ record, index }))
      .filter(({ record }) => record?.type === "biopsy_report");

    const restNonLabWithoutBiopsy = restNonLab.filter((record: any) => record?.type !== "biopsy_report");

    const biopsyGroupMap = new Map<string, Array<{ record: any; index: number }>>();
    for (const entry of biopsyEntries) {
      const dk = normalizeDateKeyFromRecord(entry.record, session.created_at);
      if (!biopsyGroupMap.has(dk)) biopsyGroupMap.set(dk, []);
      biopsyGroupMap.get(dk)!.push(entry);
    }

    const biopsyGroupItems: TimelineItem[] = Array.from(biopsyGroupMap.entries()).map(([dateKey, entries]) => {
      const sorted = [...entries].sort(
        (a, b) =>
          sortKeyForRecord(a.record, session.created_at, a.index) -
          sortKeyForRecord(b.record, session.created_at, b.index),
      );
      const ai = session.ai_summary?.trim();
      const snippetFromRecords = sorted
        .map((e) => getTextSnippet(e.record, session.ai_summary))
        .find((s) => typeof s === "string" && s.trim().length > 0 && s !== "Sem descrição");
      const textSnippet = ai || snippetFromRecords || "Sem descrição";
      const eventDate = dateKeyToSortableIso(dateKey);

      return {
        id: `${session.id}-biopsy-group-${dateKey}`,
        source: "smartfile" as const,
        eventDate,
        year: new Date(eventDate).getFullYear(),
        contentTypeLabel: "Biópsia",
        title: session.file_name || "Biópsia",
        textSnippet,
        metricLabel: "Detalhes",
        metricValue: "",
        metricCount: 0,
        sessionId: session.id,
        metrics: [],
        isBiopsyGrouped: true,
      };
    });

    const medicalEntries = restNonLabWithoutBiopsy
      .map((record: any, index: number) => ({ record, index }))
      .filter(({ record }) => isMedicalReportRecord(record));

    const restNonLabWithoutBiopsyAndMedical = restNonLabWithoutBiopsy.filter(
      (record: any) => !isMedicalReportRecord(record),
    );

    const clinicalEntries = restNonLabWithoutBiopsyAndMedical
      .map((record: any, index: number) => ({ record, index }))
      .filter(({ record }) => isClinicalRecordRecord(record));

    const restNonLabWithoutBiopsyAndMedicalAndClinical = restNonLabWithoutBiopsyAndMedical.filter(
      (record: any) => !isClinicalRecordRecord(record),
    );

    const medicalGroupMap = new Map<string, Array<{ record: any; index: number }>>();
    for (const entry of medicalEntries) {
      const dateKey = normalizeDateKeyFromRecord(entry.record, session.created_at);
      if (!medicalGroupMap.has(dateKey)) medicalGroupMap.set(dateKey, []);
      medicalGroupMap.get(dateKey)!.push(entry);
    }

    const medicalGroupItems: TimelineItem[] = Array.from(medicalGroupMap.entries()).map(([dateKey, entries]) => {
      const sorted = [...entries].sort(
        (a, b) =>
          sortKeyForRecord(a.record, session.created_at, a.index) -
          sortKeyForRecord(b.record, session.created_at, b.index),
      );
      const primaryRecord =
        sorted.find((entry) => entry.record?.structure_type !== "metrics")?.record || sorted[0].record;
      const groupedMetrics = sorted.flatMap((entry) => getMedicalReportMetrics(entry.record, session.created_at));
      const eventDate = dateKeyToSortableIso(dateKey);
      const primaryMetric = groupedMetrics[0] || { label: "Métrica", value: "Sem valor", eventDate };

      return {
        id: `${session.id}-medical-report-group-${dateKey}`,
        source: "smartfile" as const,
        eventDate,
        year: new Date(eventDate).getFullYear(),
        contentTypeLabel: "Laudo Médico",
        title:
          primaryRecord?.title ||
          primaryRecord?.report_type ||
          primaryRecord?.name ||
          session.file_name ||
          "Laudo Médico",
        textSnippet: getTextSnippet(primaryRecord, session.ai_summary),
        metricLabel: primaryMetric.label,
        metricValue: primaryMetric.value,
        metricCount: groupedMetrics.length,
        sessionId: session.id,
        metrics: groupedMetrics,
      };
    });

    const clinicalGroupItems: TimelineItem[] =
      clinicalEntries.length > 0
        ? (() => {
            const sorted = [...clinicalEntries].sort(
              (a, b) =>
                sortKeyForRecord(a.record, session.created_at, a.index) -
                sortKeyForRecord(b.record, session.created_at, b.index),
            );
            const primaryRecord =
              sorted.find((entry) => entry.record?.structure_type !== "metrics")?.record || sorted[0].record;
            const groupedMetrics = sorted.flatMap((entry) => getClinicalRecordMetrics(entry.record, session.created_at));
            const eventDate = getRecordEventDate(primaryRecord, session.created_at);
            const primaryMetric = groupedMetrics[0] || { label: "Métrica", value: "Sem valor", eventDate };

            return [
              {
                id: `${session.id}-clinical-record-group`,
                source: "smartfile" as const,
                eventDate,
                year: new Date(eventDate).getFullYear(),
                contentTypeLabel: "Registro Clínico",
                title:
                  primaryRecord?.title ||
                  primaryRecord?.note_type ||
                  primaryRecord?.name ||
                  session.file_name ||
                  "Registro Clínico",
                textSnippet: getTextSnippet(primaryRecord, session.ai_summary),
                metricLabel: primaryMetric.label,
                metricValue: primaryMetric.value,
                metricCount: groupedMetrics.length,
                sessionId: session.id,
                metrics: groupedMetrics,
              },
            ];
          })()
        : [];

    const anthroGroupMap = new Map<string, Array<{ record: any; index: number }>>();
    for (const entry of anthroEntries) {
      const dk = normalizeDateKeyFromRecord(entry.record, session.created_at);
      if (!anthroGroupMap.has(dk)) anthroGroupMap.set(dk, []);
      anthroGroupMap.get(dk)!.push(entry);
    }

    const anthroItems: TimelineItem[] = [];
    for (const [dateKey, entries] of anthroGroupMap.entries()) {
      if (entries.length === 1) {
        const { record, index } = entries[0];
        anthroItems.push(buildNonLabTimelineItem(session, record, index));
      } else {
        const sorted = [...entries].sort(
          (a, b) =>
            sortKeyForRecord(a.record, session.created_at, a.index) -
            sortKeyForRecord(b.record, session.created_at, b.index),
        );
        const metrics = sorted.flatMap((e) => collectMetricsForRecord(e.record, session.created_at));
        const first = sorted[0].record;
        const eventDate = getRecordEventDate(first, session.created_at);
        const primaryMetric = getMetric(first);

        anthroItems.push({
          id: `${session.id}-anthro-group-${dateKey}`,
          source: "smartfile",
          eventDate,
          year: new Date(eventDate).getFullYear(),
          contentTypeLabel: "Antropometria",
          title: first?.title || first?.name || session.file_name || "Antropometria",
          textSnippet: getTextSnippet(first, session.ai_summary),
          metricLabel: primaryMetric.label,
          metricValue: primaryMetric.value,
          metricCount: metrics.length,
          sessionId: session.id,
          metrics,
        });
      }
    }

    const restItems: TimelineItem[] = restNonLabWithoutBiopsyAndMedicalAndClinical.map((record: any, index: number) =>
      buildNonLabTimelineItem(session, record, index),
    );

    const nonLabItems = [...anthroItems, ...biopsyGroupItems, ...medicalGroupItems, ...clinicalGroupItems, ...restItems].sort(
      (a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime(),
    );

    return [...labItems, ...nonLabItems];
  });

  let physioGlobalItem: TimelineItem | null = null;
  if (globalPhysioEntries.length > 0) {
    const sortedEntries = [...globalPhysioEntries].sort((a, b) => {
      const aKey = sortKeyForRecord(a.record, a.sessionCreatedAt, a.index + a.globalIndex * 1e-3);
      const bKey = sortKeyForRecord(b.record, b.sessionCreatedAt, b.index + b.globalIndex * 1e-3);
      return aKey - bKey;
    });

    const physioGroupMap = new Map<string, PhysioDayGroup>();
    for (const entry of sortedEntries) {
      const dateKey = normalizeDateKeyFromRecord(entry.record, entry.sessionCreatedAt);
      if (!physioGroupMap.has(dateKey)) {
        physioGroupMap.set(dateKey, {
          dateKey,
          displayDate: format(new Date(dateKeyToSortableIso(dateKey)), "dd/MM/yyyy", { locale: ptBR }),
          observationLines: [],
          dataLines: [],
        });
      }

      const group = physioGroupMap.get(dateKey)!;
      const sk = sortKeyForRecord(entry.record, entry.sessionCreatedAt, entry.index + entry.globalIndex * 1e-3);
      if (isPhysioObservationRow(entry.record)) {
        group.observationLines.push(buildPhysioObservationLine(entry.record, entry.sessionCreatedAt, sk));
      }
      if (isPhysioDataRow(entry.record)) {
        const dataLine = buildPhysioDataLine(entry.record, entry.sessionCreatedAt, sk);
        if (dataLine) group.dataLines.push(dataLine);
      }
    }

    const days = Array.from(physioGroupMap.values())
      .map((group) => ({
        ...group,
        observationLines: group.observationLines.sort((a, b) => a.sortKey - b.sortKey),
        dataLines: group.dataLines.sort((a, b) => a.sortKey - b.sortKey),
      }))
      .filter((group) => group.observationLines.length > 0 || group.dataLines.length > 0)
      .sort((a, b) => new Date(dateKeyToSortableIso(b.dateKey)).getTime() - new Date(dateKeyToSortableIso(a.dateKey)).getTime());

    if (days.length > 0) {
      const totalObservationCount = days.reduce((acc, day) => acc + day.observationLines.length, 0);
      const totalDataCount = days.reduce((acc, day) => acc + day.dataLines.length, 0);
      const latestDateKey = days[0].dateKey;
      const eventDate = dateKeyToSortableIso(latestDateKey);
      const firstObservation = days[0].observationLines[0]?.text?.replace(/\n/g, " ").trim();
      const textSnippet =
        firstObservation ||
        (totalDataCount > 0 ? "Métricas fisioterápicas disponíveis em Observações." : "Sem descrição");

      physioGlobalItem = {
        id: "physio-global-group",
        source: "smartfile",
        eventDate,
        year: new Date(eventDate).getFullYear(),
        contentTypeLabel: "Evolução Fisioterapêutica",
        title: "Evolução Fisioterapêutica",
        textSnippet,
        metricLabel: "Observações",
        metricValue: "Ver detalhes",
        metricCount: totalObservationCount + totalDataCount,
        metrics: days.flatMap((day) => day.dataLines.map((line) => ({ label: line.label, value: line.value, eventDate: line.eventDate }))),
        physioGlobalGroup: {
          days,
          totalObservationCount,
          totalDataCount,
        },
      };
    }
  }

  const galleryItems: TimelineItem[] = galleryImages.map((image) => {
    const eventDate = image.published_at || image.created_at;
    return {
      id: `gallery-${image.id}`,
      source: "gallery",
      eventDate,
      year: new Date(eventDate).getFullYear(),
      contentTypeLabel: "Imagem",
      title: image.file_name,
      textSnippet: image.ai_summary?.trim() ? image.ai_summary : "Sem resumo",
      metricLabel: "Métrica",
      metricValue: "Sem métrica",
      metricCount: 0,
      metrics: [],
      galleryImageId: image.id,
      galleryFilePath: image.file_path,
      considerations: image.professional_notes,
    };
  });

  const pilatesItems: TimelineItem[] = pilatesRecords.map((record) => ({
    id: `pilates-${record.id}`,
    source: "pilates",
    eventDate: record.session_at || record.created_at,
    year: new Date(record.session_at || record.created_at).getFullYear(),
    contentTypeLabel: "Evolução Pilates",
    title: "Evolução Alunos Pilates",
    textSnippet: record.notes || "Sem descrição",
    metricLabel: "Métrica",
    metricValue: "Sem métrica",
    metricCount: 0,
    metrics: [],
    pilatesRecordId: record.id,
  }));

  const heartRiskItems: TimelineItem[] = heartRiskCalculations.map((calculation) => {
    const eventDate = calculation.created_at;
    const result = calculation.result_snapshot || {};
    const categoryKey = (calculation.category || result.category || "") as string;
    const categoryLabel = HEART_RISK_CATEGORY_LABEL_PT[categoryKey] || "Categoria não informada";

    const tenYear = formatHeartRiskPercent(result.tenYearRisk);
    const optimal = formatHeartRiskPercent(result.optimalTenYearRisk);
    const lifetime = formatHeartRiskPercent(result.lifetimeRisk ?? null);

    const metrics: TimelineItem["metrics"] = [];
    if (tenYear) metrics.push({ label: "Risco 10 anos", value: tenYear, eventDate });
    if (optimal) metrics.push({ label: "Risco ótimo 10 anos", value: optimal, eventDate });
    if (lifetime) metrics.push({ label: "Risco ao longo da vida", value: lifetime, eventDate });

    const snippetParts = [categoryLabel];
    if (tenYear) snippetParts.push(`Risco em 10 anos: ${tenYear}`);
    const textSnippet = snippetParts.join(" · ");

    return {
      id: `heartrisk-${calculation.id}`,
      source: "heartrisk",
      eventDate,
      year: new Date(eventDate).getFullYear(),
      contentTypeLabel: "Risco Cardiovascular",
      title: "Risco Cardiovascular (PCE)",
      textSnippet,
      metricLabel: metrics[0]?.label ?? "Resultado",
      metricValue: metrics[0]?.value ?? "—",
      metricCount: metrics.length,
      metrics,
      heartRiskCalculationId: calculation.id,
    };
  });

  const latestActiveTrainingPlan = pickLatestActiveTrainingPlan(trainingPlans);
  const trainingPlanItems: TimelineItem[] = latestActiveTrainingPlan
    ? [buildTrainingPlanTimelineItem(latestActiveTrainingPlan)]
    : [];

  const dedupedStructuredItems = dedupeTrainingPlanStructuredItems(
    structuredItems,
    trainingPlanItems.length > 0,
  );

  return [
    ...dedupedStructuredItems,
    ...trainingPlanItems,
    ...(physioGlobalItem ? [physioGlobalItem] : []),
    ...galleryItems,
    ...pilatesItems,
    ...heartRiskItems,
  ]
    .filter((item) => !Number.isNaN(new Date(item.eventDate).getTime()))
    .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
}

export function PatientTimeline({
  patientId,
  sessions,
  trainingPlans = [],
  onRefresh,
  supabaseClient,
  canDelete = false,
}: PatientTimelineProps) {
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [pilatesRecords, setPilatesRecords] = useState<PilatesEvolutionRecord[]>([]);
  const [heartRiskCalculations, setHeartRiskCalculations] = useState<HeartRiskCalculationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TimelineItem | null>(null);
  const [isGenericMetricsOpen, setIsGenericMetricsOpen] = useState(false);
  const [isLabMetricsOpen, setIsLabMetricsOpen] = useState(false);
  const [galleryModalImageId, setGalleryModalImageId] = useState<string | null>(null);
  const [biopsyDetailSessionId, setBiopsyDetailSessionId] = useState<string | null>(null);
  const [isTrainingPlanModalOpen, setIsTrainingPlanModalOpen] = useState(false);
  const [selectedTrainingPlan, setSelectedTrainingPlan] = useState<TrainingPlanDetail | null>(null);
  const [loadingTrainingPlanId, setLoadingTrainingPlanId] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<TimelineItem | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const client = supabaseClient ?? supabase;
  const { markBothStale } = useDataStale();

  const biopsyDetailSession = useMemo(
    () => (biopsyDetailSessionId ? sessions.find((s) => s.id === biopsyDetailSessionId) ?? null : null),
    [biopsyDetailSessionId, sessions],
  );

  const reloadTimelineExtras = useCallback(async () => {
    setLoading(true);
    const [galleryResult, pilatesResult, heartRiskResult] = await Promise.all([
      client
        .from("patient_gallery")
        .select("id, file_name, file_path, ai_summary, created_at, published_at, professional_notes")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false }),
      client
        .from("pilates_session_evolutions")
        .select("id, session_at, notes, created_at")
        .eq("patient_id", patientId)
        .order("session_at", { ascending: false }),
      client
        .from("patient_heart_risk_calculations")
        .select("id, created_at, model, category, result_snapshot, input_snapshot")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false }),
    ]);

    if (galleryResult.error) {
      console.error("Error loading gallery timeline", galleryResult.error);
    } else {
      setGalleryImages(galleryResult.data || []);
    }

    if (pilatesResult.error) {
      console.error("Error loading pilates timeline", pilatesResult.error);
    } else {
      setPilatesRecords(pilatesResult.data || []);
    }

    if (heartRiskResult.error) {
      console.error("Error loading heart risk timeline", heartRiskResult.error);
    } else {
      setHeartRiskCalculations((heartRiskResult.data || []) as unknown as HeartRiskCalculationRecord[]);
    }
    setLoading(false);
  }, [client, patientId]);

  useEffect(() => {
    void reloadTimelineExtras();
  }, [reloadTimelineExtras]);

  const physioSessionCount = useMemo(() => getPhysioSessionIds(sessions).length, [sessions]);

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setDeletingItemId(itemToDelete.id);
    try {
      await deleteTimelineItem(itemToDelete, {
        patientId,
        sessions,
        supabaseClient: client,
      });
      await markBothStale(patientId);
      toast.success("Registro excluído com sucesso!");
      setItemToDelete(null);
      await reloadTimelineExtras();
      await onRefresh?.();
    } catch (error) {
      console.error("Erro ao excluir registro da timeline:", error);
      toast.error("Erro ao excluir registro");
    } finally {
      setDeletingItemId(null);
    }
  };

  const items = useMemo(
    () => toTimelineItems(sessions, trainingPlans, galleryImages, pilatesRecords, heartRiskCalculations),
    [sessions, trainingPlans, galleryImages, pilatesRecords, heartRiskCalculations],
  );

  const availableFilters = useMemo(
    () => Array.from(new Set(items.map((item) => item.contentTypeLabel))),
    [items],
  );

  useEffect(() => {
    if (activeFilter === "all") return;
    if (!availableFilters.includes(activeFilter)) {
      setActiveFilter("all");
    }
  }, [activeFilter, availableFilters]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "all") return items;
    return items.filter((item) => item.contentTypeLabel === activeFilter);
  }, [items, activeFilter]);

  const sessionFilePathById = useMemo(() => {
    return new Map(
      sessions
        .filter((session) => Boolean(session.file_path))
        .map((session) => [session.id, session.file_path ?? null]),
    );
  }, [sessions]);

  const handleOpenOriginalFile = async (sessionId: string) => {
    const filePath = sessionFilePathById.get(sessionId);

    if (!filePath) {
      toast.error("Arquivo original não disponível");
      return;
    }

    const { data, error } = await client.storage
      .from("patient-docs")
      .createSignedUrl(filePath, 3600);

    if (error || !data?.signedUrl) {
      toast.error("Erro ao abrir arquivo original");
      return;
    }

    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenMetrics = (item: TimelineItem) => {
    setSelectedItem(item);

    if (item.physioGlobalGroup) {
      setIsGenericMetricsOpen(true);
      return;
    }

    if (item.metricCount <= 1) return;

    if (item.source === "smartfile" && item.contentTypeLabel === "Exame Laboratorial" && item.sessionId) {
      setIsLabMetricsOpen(true);
      return;
    }

    setIsGenericMetricsOpen(true);
  };

  const groupedTrainingPlanItems = useMemo(() => {
    if (!selectedTrainingPlan) return [];

    const map = new Map<number, TrainingPlanDetailItem[]>();
    for (const item of selectedTrainingPlan.items) {
      const existing = map.get(item.week_number) ?? [];
      existing.push(item);
      map.set(item.week_number, existing);
    }

    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([week, weekItems]) => ({
        week,
        items: [...weekItems].sort((a, b) => a.display_order - b.display_order),
      }));
  }, [selectedTrainingPlan]);

  const handleOpenTrainingPlan = async (planId: string) => {
    try {
      setLoadingTrainingPlanId(planId);

      const [planResult, itemsResult] = await Promise.all([
        client
          .from("training_plans")
          .select("id, plan_name, frequency_per_week, weekly_minutes, selected_objectives, ai_recommendations")
          .eq("id", planId)
          .maybeSingle(),
        client
          .from("training_plan_items")
          .select("id, week_number, day_of_week, session_title, exercises, duration_minutes, intensity, notes, display_order")
          .eq("training_plan_id", planId)
          .order("week_number", { ascending: true })
          .order("display_order", { ascending: true }),
      ]);

      if (planResult.error || !planResult.data) {
        toast.error("Não foi possível carregar o plano de treino");
        return;
      }

      if (itemsResult.error) {
        toast.error("Não foi possível carregar os detalhes do plano");
        return;
      }

      const aiRecommendations =
        typeof planResult.data.ai_recommendations === "object" && planResult.data.ai_recommendations !== null
          ? (planResult.data.ai_recommendations as Record<string, unknown>)
          : null;

      const summary = typeof aiRecommendations?.summary === "string" ? aiRecommendations.summary.trim() : null;

      setSelectedTrainingPlan({
        id: planResult.data.id,
        planName: planResult.data.plan_name || "Plano de Treino",
        frequencyPerWeek: planResult.data.frequency_per_week,
        weeklyMinutes: planResult.data.weekly_minutes,
        selectedObjectives: Array.isArray(planResult.data.selected_objectives)
          ? planResult.data.selected_objectives.filter((entry): entry is string => typeof entry === "string")
          : [],
        summary: summary && summary.length > 0 ? summary : null,
        items: (itemsResult.data || []).map((item) => ({
          id: item.id,
          week_number: item.week_number,
          day_of_week: item.day_of_week,
          session_title: item.session_title,
          exercises: Array.isArray(item.exercises) ? (item.exercises as Array<Record<string, unknown>>) : [],
          duration_minutes: item.duration_minutes,
          intensity: item.intensity,
          notes: item.notes,
          display_order: item.display_order,
        })),
      });
      setIsTrainingPlanModalOpen(true);
    } finally {
      setLoadingTrainingPlanId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando timeline...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center space-y-3">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhum evento na timeline</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="relative space-y-6">
      <div className="absolute left-1/2 top-0 bottom-0 hidden md:block w-px bg-border/80" />
      <div className="space-y-2 pb-1">
        <div className="w-full rounded-lg border border-border bg-gradient-to-r from-muted/70 via-muted/45 to-transparent px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Filtros</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters((current) => !current)}
              className="h-8 gap-1.5 px-2 text-muted-foreground"
            >
              {showFilters ? "Ocultar" : "Exibir"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </Button>
            {activeFilter !== "all" && !showFilters && (
              <Badge variant="outline" className="text-xs">
                {activeFilter}
              </Badge>
            )}
          </div>

          {showFilters && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {availableFilters.map((filterLabel) => {
                const isActive = activeFilter === filterLabel;
                const FilterIcon = (timelineTypeStyleMap[filterLabel] || defaultTypeStyle).Icon;

                return (
                  <Button
                    key={filterLabel}
                    type="button"
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveFilter((current) => (current === filterLabel ? "all" : filterLabel))}
                    className="h-8 gap-1.5"
                  >
                    <FilterIcon className="h-3.5 w-3.5" />
                    {filterLabel}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {filteredItems.map((item, index) => {
        const previous = filteredItems[index - 1];
        const isNewYear = !previous || previous.year !== item.year;
        const isLeft = index % 2 === 0;
        const typeStyle = timelineTypeStyleMap[item.contentTypeLabel] || defaultTypeStyle;
        const TypeIcon = typeStyle.Icon;
        const isPhysioGlobal = Boolean(item.physioGlobalGroup);
        const isBiopsyGrouped = Boolean(item.isBiopsyGrouped);
        const isGallery = item.source === "gallery";
        const hasOriginalFile =
          item.source === "smartfile" &&
          Boolean(item.sessionId) &&
          Boolean(item.sessionId && sessionFilePathById.get(item.sessionId));
        const stampFormat = isPhysioGlobal || isBiopsyGrouped ? "dd/MM/yyyy" : "dd/MM/yyyy 'às' HH:mm";
        const displayDateStr = isGallery
          ? format(new Date(item.eventDate), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
          : format(new Date(item.eventDate), stampFormat, { locale: ptBR });

        return (
          <div key={item.id} className="space-y-4">
            {isNewYear && (
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <Badge variant="outline" className="px-3 py-1 text-xs font-semibold">
                  {item.year}
                </Badge>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 items-start">
              <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-8 z-20 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />

              <div className={isLeft ? "md:pr-10" : "md:order-2 md:pl-10"}>
                <Card className={`shadow-sm transition-all duration-200 hover:shadow-md ${typeStyle.cardClassName}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2 w-full">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-semibold text-foreground/80 md:hidden">{displayDateStr}</p>
                        {!isLeft ? (
                          <Badge
                            variant="outline"
                            className={`text-xs font-semibold ${typeStyle.badgeClassName}`}
                          >
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {item.contentTypeLabel}
                          </Badge>
                        ) : null}
                        {isLeft && canDelete ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            className="h-8 w-8 p-0 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Excluir registro"
                            disabled={deletingItemId === item.id}
                            onClick={() => setItemToDelete(item)}
                          >
                            {deletingItemId === item.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isLeft ? (
                          <Badge
                            variant="outline"
                            className={`text-xs font-semibold ${typeStyle.badgeClassName}`}
                          >
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {item.contentTypeLabel}
                          </Badge>
                        ) : null}
                        {!isLeft && canDelete ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            className="h-8 w-8 p-0 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Excluir registro"
                            disabled={deletingItemId === item.id}
                            onClick={() => setItemToDelete(item)}
                          >
                            {deletingItemId === item.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {item.physioGlobalGroup ? (
                      <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                        <p>{item.textSnippet}</p>
                        <p className="text-xs text-foreground/70">
                          {item.physioGlobalGroup.totalObservationCount} observações e {item.physioGlobalGroup.totalDataCount} métricas registradas.
                        </p>
                      </div>
                    ) : isGallery ? (
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70 flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 shrink-0" />
                            Resumo IA
                          </p>
                          <p className="text-sm text-muted-foreground leading-relaxed">{item.textSnippet}</p>
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70 flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5 shrink-0" />
                            Considerações
                          </p>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.considerations?.trim()
                              ? item.considerations
                              : "Nenhuma consideração"}
                          </p>
                        </div>
                        {item.galleryImageId && (
                          <Button
                            type="button"
                            variant="link"
                            className="h-auto p-0 text-sm font-semibold text-primary"
                            onClick={() => setGalleryModalImageId(item.galleryImageId!)}
                          >
                            Ver imagem
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.textSnippet}</p>
                    )}
                    {isPhysioGlobal && (
                      <div className="rounded-md bg-muted/50 p-2 text-xs">
                        <span className="font-medium">Observações: </span>
                        <Button
                          variant="link"
                          type="button"
                          onClick={() => handleOpenMetrics(item)}
                          className="h-auto p-0 text-xs font-semibold text-primary underline-offset-2 hover:underline"
                        >
                          Ver detalhes completos
                        </Button>
                      </div>
                    )}
                    {!isPhysioGlobal && !isGallery && isBiopsyGrouped && item.sessionId && (
                      <div className="rounded-md bg-muted/50 p-2 text-xs">
                        <Button
                          variant="link"
                          type="button"
                          onClick={() => setBiopsyDetailSessionId(item.sessionId!)}
                          className="h-auto p-0 text-xs font-semibold text-primary underline-offset-2 hover:underline"
                        >
                          Detalhes
                        </Button>
                      </div>
                    )}
                    {!isPhysioGlobal && !isGallery && !isBiopsyGrouped && (
                      <div className="rounded-md bg-muted/50 p-2 text-xs">
                        <span className="font-medium">{item.metricCount > 1 ? "Métricas" : item.metricLabel}: </span>
                        {item.metricCount > 1 ? (
                          <Button
                            variant="link"
                            type="button"
                            onClick={() => handleOpenMetrics(item)}
                            className="h-auto p-0 text-xs font-semibold text-primary underline-offset-2 hover:underline"
                          >
                            Ver os {item.metricCount} resultados
                          </Button>
                        ) : (
                          <span>{item.metricValue}</span>
                        )}
                      </div>
                    )}
                    {item.contentTypeLabel === "Plano de Treino" && item.trainingPlanId && (
                      <div className="flex justify-end">
                        <Button
                          variant="link"
                          type="button"
                          onClick={() => void handleOpenTrainingPlan(item.trainingPlanId!)}
                          disabled={loadingTrainingPlanId === item.trainingPlanId}
                          className="h-auto p-0 text-xs font-semibold text-primary underline-offset-2 hover:underline"
                        >
                          {loadingTrainingPlanId === item.trainingPlanId ? "Abrindo..." : "Abrir plano"}
                        </Button>
                      </div>
                    )}
                    {hasOriginalFile && item.sessionId && (
                      <div className="flex justify-end">
                        <Button
                          variant="link"
                          type="button"
                          onClick={() => {
                            void handleOpenOriginalFile(item.sessionId!);
                          }}
                          className="h-auto p-0 text-xs font-semibold text-primary underline-offset-2 hover:underline"
                        >
                          Ver arquivo original
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div
                className={
                  isLeft
                    ? "hidden md:flex md:pl-10 md:pt-2"
                    : "hidden md:flex md:order-1 md:pr-10 md:pt-2 md:justify-end"
                }
              >
                <p className="text-base font-semibold text-foreground/80 whitespace-nowrap">{displayDateStr}</p>
              </div>
            </div>
          </div>
        );
      })}

      {filteredItems.length === 0 && (
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-center text-muted-foreground">
              Nenhum evento encontrado para o filtro selecionado.
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={isGenericMetricsOpen} onOpenChange={setIsGenericMetricsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedItem?.physioGlobalGroup
                ? "Observações - Evolução Fisioterapêutica"
                : selectedItem?.contentTypeLabel || "Resultados"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {selectedItem?.physioGlobalGroup ? (
              <div className="space-y-4">
                {selectedItem.physioGlobalGroup.days.map((day) => (
                  <div key={day.dateKey} className="rounded-md border p-3 space-y-3">
                    <p className="text-sm font-semibold text-foreground">{day.displayDate}</p>
                    {day.observationLines.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">Observações</p>
                        <ul className="space-y-2 list-none pl-0">
                          {day.observationLines.map((line, idx) => (
                            <li key={`${day.dateKey}-obs-${idx}`} className="text-sm whitespace-pre-wrap text-muted-foreground">
                              {line.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {day.dataLines.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">Métricas</p>
                        <ul className="space-y-1 list-none pl-0">
                          {day.dataLines.map((line, idx) => (
                            <li key={`${day.dateKey}-data-${idx}`} className="text-sm">
                              <span className="font-medium text-foreground/85">{line.label}</span>
                              <span className="text-muted-foreground"> — {line.value}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {day.observationLines.length === 0 && day.dataLines.length === 0 && (
                      <p className="text-sm text-muted-foreground">Sem registros nesta data.</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {(selectedItem?.metrics || []).map((metric, idx) => (
                  <div key={`${metric.label}-${idx}`} className="rounded-md border p-3">
                    <p className="text-sm font-semibold">{metric.label}</p>
                    <p className="text-sm text-muted-foreground">{metric.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(metric.eventDate), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isTrainingPlanModalOpen}
        onOpenChange={(open) => {
          setIsTrainingPlanModalOpen(open);
          if (!open) setSelectedTrainingPlan(null);
        }}
      >
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTrainingPlan?.planName || "Plano de Treino"}</DialogTitle>
          </DialogHeader>

          {selectedTrainingPlan ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                {selectedTrainingPlan.summary && (
                  <p className="text-sm text-muted-foreground leading-relaxed">{selectedTrainingPlan.summary}</p>
                )}
                <p className="text-sm">
                  <span className="font-medium">Frequência:</span>{" "}
                  {selectedTrainingPlan.frequencyPerWeek && selectedTrainingPlan.weeklyMinutes
                    ? `${selectedTrainingPlan.frequencyPerWeek}x/semana · ${selectedTrainingPlan.weeklyMinutes} min/semana`
                    : "Sem frequência definida"}
                </p>
              </div>

              {selectedTrainingPlan.selectedObjectives.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">Objetivos</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedTrainingPlan.selectedObjectives.map((objective) => (
                      <Badge key={objective} variant="secondary" className="font-normal">
                        {objective}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {groupedTrainingPlanItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma sessão encontrada neste plano.</p>
              ) : (
                <div className="space-y-4">
                  {groupedTrainingPlanItems.map((weekGroup) => (
                    <div key={`week-${weekGroup.week}`} className="rounded-md border p-3 space-y-3">
                      <p className="text-sm font-semibold">Semana {weekGroup.week}</p>

                      <div className="space-y-3">
                        {weekGroup.items.map((sessionItem) => (
                          <div key={sessionItem.id} className="rounded-md bg-muted/40 p-3 space-y-2">
                            <p className="text-sm font-semibold">{sessionItem.session_title}</p>
                            <p className="text-xs text-muted-foreground">
                              {sessionItem.day_of_week}
                              {sessionItem.duration_minutes ? ` · ${sessionItem.duration_minutes} min` : ""}
                              {sessionItem.intensity ? ` · ${sessionItem.intensity}` : ""}
                            </p>

                            {sessionItem.exercises.length > 0 ? (
                              <ul className="space-y-1 list-disc pl-4">
                                {sessionItem.exercises.map((exercise, exerciseIndex) => {
                                  const exerciseData = getExerciseRowData(exercise);
                                  return (
                                    <li key={`${sessionItem.id}-${exerciseIndex}`} className="text-sm">
                                      <span className="font-medium">{exerciseData.name}</span>
                                      <span className="text-muted-foreground"> — {exerciseData.details}</span>
                                      {exerciseData.notes ? (
                                        <span className="text-muted-foreground"> ({exerciseData.notes})</span>
                                      ) : null}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p className="text-sm text-muted-foreground">Sem exercícios detalhados nesta sessão.</p>
                            )}

                            {sessionItem.notes ? (
                              <p className="text-sm text-muted-foreground italic">{sessionItem.notes}</p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Plano não disponível.</p>
          )}
        </DialogContent>
      </Dialog>

      {selectedItem?.sessionId && (
        <RecordResultsModal
          isOpen={isLabMetricsOpen}
          onClose={() => setIsLabMetricsOpen(false)}
          sessionId={selectedItem.sessionId}
          supabaseClient={client}
        />
      )}

      <GalleryImageModal
        isOpen={galleryModalImageId !== null}
        onClose={() => setGalleryModalImageId(null)}
        imageId={galleryModalImageId ?? ""}
        patientId={patientId}
        supabaseClient={client}
      />

      <AlertDialog open={itemToDelete !== null} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete
                ? getTimelineDeleteDescription(itemToDelete, physioSessionCount)
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingItemId !== null}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteConfirm();
              }}
              disabled={deletingItemId !== null}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingItemId !== null ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={biopsyDetailSessionId !== null && biopsyDetailSession !== null}
        onOpenChange={(open) => {
          if (!open) setBiopsyDetailSessionId(null);
        }}
      >
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] max-h-[min(90vh,900px)] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b">
            <DialogTitle className="text-left">
              {biopsyDetailSession?.file_name || "Biópsia"}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 py-4 min-h-0 flex-1">
            {biopsyDetailSession && (
              <SmartfileBiopsyDetails
                session={{
                  ...biopsyDetailSession,
                  patient_id: biopsyDetailSession.patient_id ?? null,
                  status: biopsyDetailSession.status ?? null,
                }}
                supabaseClient={client}
                onRefresh={() => {
                  onRefresh?.();
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
